var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
/**
 * The field detail's subfield table with the label trace folded into it (CZNA-547).
 *
 * <p>Until CZNA-547 the trace was its own table below the subfield table and the binds
 * table: a fourth view in which a reader asking about ONE subfield had to find that
 * subfield's rows among 24 others and hold them against the table above. NK read it as
 * unreadable and proposed the fix this implements — the trace is a statement about a
 * row, so it stands under that row, collapsed until asked for.
 *
 * <p>Each row carries a "Ve formuláři" cell with the trace's own source badges (the
 * same states and colours the separate table used — what they mean is CZNA-461's, not
 * this ticket's) so the reader sees at a glance which rows the form names differently,
 * and one click opens the per-type detail for exactly that row.
 *
 * <p>Three kinds of row, because three things carry a trace:
 * - the field itself (the name the form puts on the whole field),
 * - every subfield the ontology declares,
 * - every subfield a template declares that the ontology does NOT — a name that
 *   exists only in a template (`ONTOLOGY_MISSING`) or a frontend-only input
 *   (`FORM_ONLY`). Dropping those because they have no ontology row would hide the
 *   one case the trace most wants seen.
 *
 * <p>A plain table, not `MultiColumnList`: MCL has no expandable rows, and a detail row
 * spanning every column is what the proposal is.
 */
import React, { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { MultiColumnList } from '@folio/stripes/components';
import HelpTooltip from '../components/HelpTooltip';
import SubfieldDescriptionCell from './SubfieldDescriptionCell';
import { isPlaceholderSubfieldLabel } from './semanticCatalogFieldKind';
import { SOURCE_COLOR, badgeStyle } from './semanticCatalogBadges';
import { isDiscrepancySource } from './labelTraceSummary';
var MSG = 'ui-czna-authorities.semanticCatalog';
/** The one key the field-level row uses; subfield rows use their code. */
var FIELD_ROW = '';
var cellStyle = {
    padding: '6px 8px',
    borderBottom: '1px solid #e5e5e5',
    verticalAlign: 'top',
    textAlign: 'left',
};
var headStyle = __assign(__assign({}, cellStyle), { fontWeight: 600, backgroundColor: '#f5f5f5', borderBottom: '1px solid #d8d8d8', whiteSpace: 'nowrap' });
var muted = { color: '#666' };
/**
 * Distinct sources of a row's trace, each once with how often it occurs — in the
 * order `SOURCE_COLOR` declares them, so the same combination always reads the same.
 */
var sourceTally = function (rows) { return (Object.keys(SOURCE_COLOR)
    .map(function (source) { return [source, rows.filter(function (row) { return row.source === source; }).length]; })
    .filter(function (_a) {
    var count = _a[1];
    return count > 0;
})); };
var SubfieldTraceTable = function (_a) {
    var _b;
    var tag = _a.tag, fieldLabel = _a.fieldLabel, subfields = _a.subfields, trace = _a.trace;
    var intl = useIntl();
    var _c = useState(new Set()), expanded = _c[0], setExpanded = _c[1];
    var traceRows = (trace === null || trace === void 0 ? void 0 : trace.found) ? ((_b = trace.rows) !== null && _b !== void 0 ? _b : []) : undefined;
    var rowsFor = function (code) { return (traceRows !== null && traceRows !== void 0 ? traceRows : []).filter(function (row) { return row.code === code; }); };
    var declared = new Set(subfields.map(function (sub) { return sub.code; }));
    var templateOnly = Array.from(new Set((traceRows !== null && traceRows !== void 0 ? traceRows : []).map(function (row) { return row.code; }).filter(function (code) { return !!code && !declared.has(code); })));
    var rows = __spreadArray(__spreadArray([
        { key: FIELD_ROW, code: null }
    ], subfields.map(function (sub) { return ({ key: sub.code, code: sub.code, subfield: sub }); }), true), templateOnly.map(function (code) { return ({ key: code, code: code }); }), true);
    var toggle = function (key) { return setExpanded(function (prev) {
        var next = new Set(prev);
        if (next.has(key))
            next.delete(key);
        else
            next.add(key);
        return next;
    }); };
    var codeLabel = function (row) { return (row.code === null ? tag : "$".concat(row.code)); };
    var nameCell = function (row) {
        var _a, _b, _c;
        if (row.code === null) {
            return (React.createElement(React.Fragment, null,
                React.createElement("span", { style: muted },
                    React.createElement(FormattedMessage, { id: "".concat(MSG, ".subfieldTrace.fieldRow") })),
                fieldLabel ? React.createElement("div", null, fieldLabel) : null));
        }
        if (!row.subfield) {
            return (React.createElement("span", { style: __assign(__assign({}, muted), { fontStyle: 'italic' }) },
                React.createElement(FormattedMessage, { id: "".concat(MSG, ".subfieldTrace.notInOntology") })));
        }
        // 422 of the catalog's 583 subfields were once named `Podpole $a` — the generator's
        // stand-in, not a name (CZNA-446). The screen says the name is missing instead.
        if (isPlaceholderSubfieldLabel((_a = row.subfield.label) === null || _a === void 0 ? void 0 : _a.cs, row.subfield.code)) {
            return (React.createElement("span", { style: __assign(__assign({}, muted), { fontStyle: 'italic' }) },
                React.createElement(FormattedMessage, { id: "".concat(MSG, ".subfieldUnnamed") })));
        }
        return (_c = (_b = row.subfield.label) === null || _b === void 0 ? void 0 : _b.cs) !== null && _c !== void 0 ? _c : '—';
    };
    var formCell = function (row) {
        if (!traceRows)
            return React.createElement("span", { style: muted }, "\u2026");
        var own = rowsFor(row.code);
        if (!own.length) {
            return (React.createElement("span", { style: muted },
                React.createElement(FormattedMessage, { id: "".concat(MSG, ".subfieldTrace.inNoForm") })));
        }
        // The same rule the fields list's tally counts by, so a field the tally lists
        // always opens onto at least one row marked here — and an agreeing row never is.
        var discrepant = own.some(isDiscrepancySource);
        return (React.createElement("span", { style: { display: 'inline-flex', flexWrap: 'wrap', gap: '4px' } },
            discrepant && (React.createElement("span", { "data-testid": "discrepancy-badge-".concat(row.key || 'field'), style: badgeStyle('#c00') },
                React.createElement(FormattedMessage, { id: "".concat(MSG, ".subfieldTrace.discrepancy") }))),
            sourceTally(own).map(function (_a) {
                var source = _a[0], count = _a[1];
                return (React.createElement("span", { key: source, "data-testid": "trace-badge-".concat(source), style: badgeStyle(SOURCE_COLOR[source]) },
                    intl.formatMessage({ id: "".concat(MSG, ".source.").concat(source) }),
                    count > 1 ? " \u00D7".concat(count) : null));
            })));
    };
    /**
     * The per-type rows of one table row — the columns the separate trace table had,
     * minus the subfield code, which the row above already says.
     */
    var detail = function (row) { return (
    // @ts-expect-error - stripes MCL types require all entity keys but only visible columns are needed
    React.createElement(MultiColumnList, { id: "ontology-trace-".concat(row.key || 'field'), contentData: rowsFor(row.code), visibleColumns: ['type', 'occurrence', 'template', 'ontology', 'source'], columnMapping: {
            type: intl.formatMessage({ id: "".concat(MSG, ".col.authorityType") }),
            occurrence: intl.formatMessage({ id: "".concat(MSG, ".col.occurrence") }),
            template: intl.formatMessage({ id: "".concat(MSG, ".col.inTemplate") }),
            ontology: intl.formatMessage({ id: "".concat(MSG, ".col.inOntology") }),
            source: intl.formatMessage({ id: "".concat(MSG, ".col.labelSource") }),
        }, formatter: {
            type: function (traceRow) { return (React.createElement(React.Fragment, null,
                traceRow.type,
                traceRow.customized && (React.createElement("span", { title: intl.formatMessage({ id: "".concat(MSG, ".trace.customized") }) }, ' ✎')))); },
            // Without this the eight identical PERSONAL rows tag 024 produces per subfield
            // are indistinguishable — same name, type and source.
            occurrence: function (traceRow) { return "#".concat(traceRow.occurrence + 1); },
            // Inheriting is not a missing value: the form DOES show a name, the
            // ontology's. Rendering it as '—' would read as "no name anywhere".
            template: function (traceRow) {
                var _a;
                return ((_a = traceRow.template) !== null && _a !== void 0 ? _a : (React.createElement("span", { style: muted },
                    React.createElement(FormattedMessage, { id: "".concat(MSG, ".trace.inherits") }))));
            },
            ontology: function (traceRow) { var _a; return (_a = traceRow.ontology) !== null && _a !== void 0 ? _a : '—'; },
            source: function (traceRow) {
                var _a;
                return (React.createElement("span", { style: badgeStyle((_a = SOURCE_COLOR[traceRow.source]) !== null && _a !== void 0 ? _a : '#666') }, intl.formatMessage({ id: "".concat(MSG, ".source.").concat(traceRow.source) })));
            },
        } })); };
    var columns = 8;
    return (React.createElement("table", { id: "ontology-subfields", style: { width: '100%', borderCollapse: 'collapse' } },
        React.createElement("thead", null,
            React.createElement("tr", null,
                React.createElement("th", { style: __assign(__assign({}, headStyle), { width: '2rem' }), "aria-label": intl.formatMessage({ id: "".concat(MSG, ".subfieldTrace.toggleColumn") }) }),
                React.createElement("th", { style: headStyle }, intl.formatMessage({ id: "".concat(MSG, ".col.subfield") })),
                React.createElement("th", { style: headStyle }, intl.formatMessage({ id: "".concat(MSG, ".col.label") })),
                React.createElement("th", { style: headStyle }, intl.formatMessage({ id: "".concat(MSG, ".col.repeatable") })),
                React.createElement("th", { style: headStyle }, intl.formatMessage({ id: "".concat(MSG, ".col.mandatory") })),
                React.createElement("th", { style: headStyle }, intl.formatMessage({ id: "".concat(MSG, ".col.inputPolicy") })),
                React.createElement("th", { style: headStyle },
                    React.createElement("span", { style: { display: 'inline-flex', alignItems: 'center' } },
                        React.createElement(FormattedMessage, { id: "".concat(MSG, ".col.inForm") }),
                        React.createElement(HelpTooltip, { messageId: "".concat(MSG, ".help.traceTable"), testKey: "traceTable", idPrefix: "ontology-help" }))),
                React.createElement("th", { style: headStyle }, intl.formatMessage({ id: "".concat(MSG, ".col.description") })))),
        React.createElement("tbody", null, rows.map(function (row) {
            var isOpen = expanded.has(row.key);
            var hasTrace = rowsFor(row.code).length > 0;
            var detailId = "ontology-trace-row-".concat(row.key || 'field');
            return (React.createElement(React.Fragment, { key: row.key || '__field' },
                React.createElement("tr", { "data-testid": "subfield-row-".concat(row.key || 'field') },
                    React.createElement("td", { style: cellStyle }, hasTrace && (React.createElement("button", { type: "button", onClick: function () { return toggle(row.key); }, "aria-expanded": isOpen, "aria-controls": isOpen ? detailId : undefined, "aria-label": intl.formatMessage({ id: "".concat(MSG, ".subfieldTrace.toggle") }, { code: codeLabel(row) }), "data-testid": "trace-toggle-".concat(row.key || 'field'), style: {
                            border: 'none', background: 'none', cursor: 'pointer', padding: 0, fontSize: '0.9rem',
                        } }, isOpen ? '▾' : '▸'))),
                    React.createElement("td", { style: cellStyle }, row.code === null ? React.createElement("strong", null, tag) : React.createElement("code", null, codeLabel(row))),
                    React.createElement("td", { style: cellStyle }, nameCell(row)),
                    React.createElement("td", { style: cellStyle }, row.subfield ? (row.subfield.repeatable_default ? '✓' : '–') : ''),
                    React.createElement("td", { style: cellStyle }, row.subfield ? (row.subfield.mandatory ? '✓' : '–') : ''),
                    React.createElement("td", { style: cellStyle }, row.subfield ? (row.subfield.input_policy === 'forbidden' ? '⛔' : '–') : ''),
                    React.createElement("td", { style: cellStyle }, formCell(row)),
                    React.createElement("td", { style: __assign(__assign({}, cellStyle), { minWidth: '16rem' }) }, row.subfield ? React.createElement(SubfieldDescriptionCell, { tag: tag, code: row.subfield.code }) : null)),
                isOpen && hasTrace && (React.createElement("tr", { id: detailId, "data-testid": detailId },
                    React.createElement("td", { style: __assign(__assign({}, cellStyle), { backgroundColor: '#fafafa' }), colSpan: columns },
                        React.createElement("div", { style: __assign(__assign({}, muted), { fontSize: '0.85rem', marginBottom: '4px' }) },
                            React.createElement(FormattedMessage, { id: "".concat(MSG, ".detail.trace") })),
                        detail(row))))));
        }))));
};
export default SubfieldTraceTable;
